/*
 * Rider Analysis — Phase 1
 *   • Platform Analysis sub-tab
 *       - Orders per platform per month (line chart)
 *       - Rider count per platform per month (line chart)
 *       - Net salary per platform per month (bar chart)
 *       - Battery-swap × Platform breakdown (stacked bar + table)
 *   • Individual Rider Analysis sub-tab — placeholder for Phase 2.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { RefreshCw, Users as UsersIcon, PackageOpen, Wallet, MapPin } from 'lucide-react';

// Curated colour palette per platform (stable ordering across charts).
const PLATFORM_COLORS = {
  Talabat: '#FF6A00',
  Noon: '#FEDD00',
  Careem: '#00B95C',
  Deliveroo: '#00CCBC',
  Keeta: '#FFCE00',
  Uber: '#0A0A0A',
  Instashop: '#FFD400',
  Unassigned: '#94A3B8',
};
function colorFor(pl, idx = 0) {
  return PLATFORM_COLORS[pl] || ['#2563EB', '#7C3AED', '#DB2777', '#EA580C', '#0891B2', '#65A30D'][idx % 6];
}

/** Reshape server response `{orders, rider_count, net_salary}` into
 *  recharts-friendly row arrays keyed by month.
 */
function pivotByMonth(data, metric) {
  if (!data?.months?.length) return [];
  return data.months.map((mk) => {
    const row = { month: mk };
    (data.platforms || []).forEach((pl) => {
      row[pl] = Number(data[metric]?.[pl]?.[mk] || 0);
    });
    return row;
  });
}

const PlatformAnalysis = () => {
  const [inHouseOnly, setInHouseOnly] = useState(true);
  const [months, setMonths] = useState(12);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [swapBreakdown, setSwapBreakdown] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        months: String(months),
        in_house_only: inHouseOnly ? 'true' : 'false',
      });
      const [s1, s2] = await Promise.all([
        axios.get(`${API}/analysis/platform/monthly-summary?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API}/analysis/platform/swap-location-breakdown?in_house_only=${inHouseOnly}`, { headers: getAuthHeader() }),
      ]);
      setSummary(s1.data);
      setSwapBreakdown(s2.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Platform Analysis');
    } finally {
      setLoading(false);
    }
  }, [inHouseOnly, months]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const ordersRows = useMemo(() => pivotByMonth(summary, 'orders'), [summary]);
  const riderRows = useMemo(() => pivotByMonth(summary, 'rider_count'), [summary]);
  const netSalaryRows = useMemo(() => pivotByMonth(summary, 'net_salary'), [summary]);
  const platforms = summary?.platforms || [];

  return (
    <div className="space-y-4" data-testid="platform-analysis">
      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 py-3">
          <div className="flex items-center gap-2">
            <Switch
              id="inhouse-only"
              checked={inHouseOnly}
              onCheckedChange={setInHouseOnly}
              data-testid="platform-inhouse-toggle"
            />
            <Label htmlFor="inhouse-only" className="text-sm">In-house lessees only</Label>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Months</Label>
            <select
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              className="h-8 text-sm border rounded px-2"
              data-testid="platform-months-select"
            >
              {[3, 6, 12, 18, 24].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {loading && <Badge className="bg-sky-100 text-sky-700 border border-sky-200"><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Loading…</Badge>}
            <Button size="sm" variant="outline" onClick={fetchAll} data-testid="platform-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Chart 1 — orders per platform per month */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <PackageOpen className="w-4 h-4 text-emerald-600" /> Monthly orders — per platform
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={ordersRows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <ReTooltip />
                <Legend />
                {platforms.map((pl, i) => (
                  <Line key={pl} type="monotone" dataKey={pl} stroke={colorFor(pl, i)} strokeWidth={2} dot={{ r: 3 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Chart 2 — active riders per platform per month */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-blue-600" /> Active riders — per platform, per month
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={riderRows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <ReTooltip />
                <Legend />
                {platforms.map((pl, i) => (
                  <Line key={pl} type="monotone" dataKey={pl} stroke={colorFor(pl, i)} strokeWidth={2} dot={{ r: 3 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Chart 3 — net salary per platform per month */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet className="w-4 h-4 text-amber-600" /> Net salary paid — per platform, per month (AED)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={netSalaryRows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <ReTooltip formatter={(v) => `AED ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`} />
                <Legend />
                {platforms.map((pl, i) => (
                  <Bar key={pl} dataKey={pl} stackId="salary" fill={colorFor(pl, i)} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Chart 4 — battery swap location × platform breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="w-4 h-4 text-rose-600" /> Battery-swap location × platform breakdown
          </CardTitle>
          <p className="text-[11px] text-slate-500">Counts of swaps per location, split by the rider&apos;s current platform.</p>
        </CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={swapBreakdown?.matrix || []} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="location" width={140} tick={{ fontSize: 11 }} />
                <ReTooltip />
                <Legend />
                {(swapBreakdown?.platforms || []).map((pl, i) => (
                  <Bar key={pl} dataKey={pl} stackId="swaps" fill={colorFor(pl, i)} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Detail table */}
          {swapBreakdown?.matrix?.length > 0 && (
            <div className="mt-3 overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2 font-medium text-slate-600">Location</th>
                    {swapBreakdown.platforms.map((pl) => (
                      <th key={pl} className="text-right p-2 font-medium text-slate-600">{pl}</th>
                    ))}
                    <th className="text-right p-2 font-medium text-slate-800">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {swapBreakdown.matrix.map((row) => (
                    <tr key={row.location} className="border-t hover:bg-slate-50">
                      <td className="p-2 font-medium text-slate-700">{row.location}</td>
                      {swapBreakdown.platforms.map((pl) => (
                        <td key={pl} className="p-2 text-right tabular-nums">{row[pl] || 0}</td>
                      ))}
                      <td className="p-2 text-right font-semibold tabular-nums">{row._total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** Individual Rider Analysis — dropdown + KYC score + 10 charts.
 *  Loads a single rider's full analytics from
 *  `GET /api/analysis/rider/{id}?range=…` in one round-trip.
 */
const IndividualRiderAnalysis = () => {
  const [riders, setRiders] = useState([]);
  const [inHouseOnly, setInHouseOnly] = useState(true);
  const [riderId, setRiderId] = useState('');
  const [range, setRange] = useState('1m');
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const loadRiders = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/analysis/rider/list?in_house_only=${inHouseOnly}`,
        { headers: getAuthHeader() },
      );
      setRiders(res.data.rows || []);
      if (!res.data.rows?.some((r) => r.id === riderId)) {
        setRiderId(res.data.rows?.[0]?.id || '');
        setPayload(null);
      }
    } catch (e) {
      toast.error('Failed to load rider list');
    }
  }, [inHouseOnly, riderId]);

  const loadRider = useCallback(async () => {
    if (!riderId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/analysis/rider/${riderId}?range=${range}`,
        { headers: getAuthHeader() },
      );
      setPayload(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load rider analysis');
    } finally {
      setLoading(false);
    }
  }, [riderId, range]);

  useEffect(() => { loadRiders(); }, [loadRiders]);
  useEffect(() => { loadRider(); }, [loadRider]);

  const kyc = payload?.kyc;
  const s = payload?.series || {};

  const kycColour = (pct) => {
    if (pct >= 80) return 'bg-emerald-500';
    if (pct >= 60) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  return (
    <div className="space-y-4" data-testid="individual-rider-analysis">
      {/* Selector bar */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 py-3">
          <div className="flex items-center gap-2">
            <Switch id="ind-inhouse" checked={inHouseOnly} onCheckedChange={setInHouseOnly} data-testid="ind-inhouse-toggle" />
            <Label htmlFor="ind-inhouse" className="text-sm">In-house lessees only</Label>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Rider</Label>
            <select
              value={riderId}
              onChange={(e) => setRiderId(e.target.value)}
              className="h-8 text-sm border rounded px-2 min-w-[260px]"
              data-testid="ind-rider-select"
            >
              <option value="">— Select rider —</option>
              {riders.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} · {r.rider_unique_id || '—'} · {r.platform || '-'} {r.lessee_company ? `· ${r.lessee_company}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1 ml-2">
            {['1d', '1w', '1m', '1y', 'all'].map((rk) => (
              <Button
                key={rk} size="sm"
                variant={range === rk ? 'default' : 'outline'}
                onClick={() => setRange(rk)}
                className="h-7 px-3 text-xs uppercase"
                data-testid={`ind-range-${rk}`}
              >{rk === 'all' ? 'Till date' : rk}</Button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {loading && <Badge className="bg-sky-100 text-sky-700 border border-sky-200"><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Loading…</Badge>}
            <Button size="sm" variant="outline" onClick={loadRider}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          </div>
        </CardContent>
      </Card>

      {!payload ? (
        <Card><CardContent className="py-10 text-center text-slate-400 text-sm">Select a rider to see the analysis.</CardContent></Card>
      ) : (
        <>
          {/* KYC score */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">KYC completeness</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-3xl font-bold">{kyc?.score_percent ?? 0}%</div>
                <div className="text-xs text-slate-500">
                  {kyc?.earned || 0} of {kyc?.total_weight || 100} points earned
                </div>
                <div className="flex-1 mx-3 h-3 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`${kycColour(kyc?.score_percent || 0)} h-full`} style={{ width: `${kyc?.score_percent || 0}%` }} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1 text-xs">
                {(kyc?.breakdown || []).map((b) => (
                  <div key={b.label} className={`flex items-center gap-2 py-0.5 ${b.present ? 'text-slate-700' : 'text-slate-400 line-through'}`}>
                    <span className={`inline-block w-2 h-2 rounded-full ${b.present ? 'bg-emerald-500' : 'bg-slate-300'}`}></span>
                    <span className="flex-1 truncate">{b.label}</span>
                    <span className="text-slate-500 tabular-nums">+{b.earned}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Row 1: Orders + Gross + Net */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SimpleBar title="Monthly orders" data={s.monthly_orders} dataKey="value" color="#059669" />
            <SimpleBar title="Gross monthly earnings (AED)" data={s.gross_earnings} dataKey="value" color="#2563EB" />
            <SimpleBar title="Net monthly earnings (AED)" data={s.net_earnings} dataKey="value" color="#7C3AED" />
          </div>

          {/* Row 2: Fines + Loan repayment + Warnings */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SimpleBar title="Monthly fines (AED)" data={s.fines} dataKey="total" color="#DC2626" />
            <SimpleBar title="Monthly loan repayment (AED)" data={s.loan_repayment} dataKey="value" color="#F59E0B" />
            <SimpleBar title="Monthly warnings (count)" data={s.warnings} dataKey="count" color="#EA580C" />
          </div>

          {/* Row 3: Orders vs KMs */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Orders vs KMs driven (daily)</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <LineChart data={s.orders_vs_kms || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
                    <ReTooltip />
                    <Legend />
                    <Line yAxisId="l" type="monotone" dataKey="orders" stroke="#059669" name="Orders" dot={false} />
                    <Line yAxisId="r" type="monotone" dataKey="kms" stroke="#2563EB" name="KMs" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Row 4: Bike history + Expenses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Bike assignment history</CardTitle></CardHeader>
              <CardContent>
                {(s.bike_history || []).length === 0 ? (
                  <div className="text-xs text-slate-400 py-6 text-center">No bike changes recorded</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">From</th>
                        <th className="text-left p-2">To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(s.bike_history || []).map((h, i) => (
                        <tr key={i} className="border-t"><td className="p-2 whitespace-nowrap">{h.date}</td><td className="p-2">{h.from}</td><td className="p-2 font-medium">{h.to}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Rider expenses</CardTitle></CardHeader>
              <CardContent>
                {(s.expenses || []).length === 0 ? (
                  <div className="text-xs text-slate-400 py-6 text-center">No expenses recorded</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Item</th>
                        <th className="text-right p-2">Amount (AED)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(s.expenses || []).slice(0, 40).map((e, i) => (
                        <tr key={i} className="border-t"><td className="p-2 whitespace-nowrap">{e.date}</td><td className="p-2">{e.item}</td><td className="p-2 text-right tabular-nums">{e.amount.toLocaleString()}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row 5: Platform history */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Platform history</CardTitle></CardHeader>
            <CardContent>
              {(s.platform_history || []).length === 0 ? (
                <div className="text-xs text-slate-400 py-4 text-center">No platform records</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(s.platform_history || []).map((p) => (
                    <div key={p.month} className="text-xs px-2 py-1 rounded border" style={{ background: colorFor(p.platform) + '22', borderColor: colorFor(p.platform) }}>
                      <span className="font-medium">{p.month}</span> · {p.platform}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

/** Compact re-usable bar chart card. */
const SimpleBar = ({ title, data, dataKey, color }) => (
  <Card>
    <CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
    <CardContent>
      <div style={{ width: '100%', height: 220 }}>
        {(!data || data.length === 0) ? (
          <div className="text-xs text-slate-400 h-full flex items-center justify-center">No data in this range</div>
        ) : (
          <ResponsiveContainer>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <ReTooltip />
              <Bar dataKey={dataKey} fill={color} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </CardContent>
  </Card>
);

/** Rider Analysis page — houses the Platform Analysis and Individual
 *  Rider Analysis sub-tabs.
 */
const RiderAnalysis = () => {
  const [tab, setTab] = useState('platform');
  return (
    <div className="space-y-4" data-testid="rider-analysis-page">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap w-full h-auto justify-start gap-1 p-1">
          <TabsTrigger value="platform" data-testid="ra-tab-platform" className="min-w-[160px]">A. Platform Analysis</TabsTrigger>
          <TabsTrigger value="rider" data-testid="ra-tab-rider" className="min-w-[180px]">B. Individual Rider Analysis</TabsTrigger>
        </TabsList>
        <TabsContent value="platform" className="mt-4">
          <PlatformAnalysis />
        </TabsContent>
        <TabsContent value="rider" className="mt-4">
          <IndividualRiderAnalysis />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RiderAnalysis;
